import type { ICandidate } from "@bbj-types/candidate";
import type { IContact } from "@bbj-types/contact";
import type { IOffer } from "@bbj-types/offer";
import type { ICreateOfferPayloadOffer } from "@bbj-types/offer/create/index.d";
import type { ObjectId } from "@bbj-types/utils";
import { useCallback } from "react";
import { axios, axiosInstance } from "./axios";
import type { ISocialOfferWithFavoriteChallenges } from "./socialOffer/types";

/**
 * Route API V2
 */

export interface IUsePostOffersSearchByCandidateBody {
  totalLimit: number;
  offersLimit: number;
  skip: number;
  [key: string]: unknown;
}

export interface IUsePostOffersSearchByCandidateResponse {
  data: IOffer[];
  limit: number;
  skip: number;
  total_limit: number;
  offers_limit: number;
  total: number;
  count: number;
  orderStrategy: {
    strategy: string;
    model: string;
    candidate: ICandidate;
  };
  metadata: {
    filtersHash: string;
  };
}

export const usePostOffersSearchByCandidate = () => {
  const [state, execute, cancel] = axios<
    IUsePostOffersSearchByCandidateResponse,
    IUsePostOffersSearchByCandidateBody,
    unknown
  >(
    {
      url: "/v2/offers/search",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    ({
      kind,
      candidateId,
      ...filters
    }: IUsePostOffersSearchByCandidateBody & { kind: string; candidateId: string }) => {
      if (kind && candidateId) {
        return execute({
          url: `/v2/offers/search/${kind}/candidate/${candidateId}`,
          data: filters,
        });
      }
      return null;
    },
    [execute]
  );
  return [state, callback, cancel] as const;
};

interface IFeedbackData {
  recommendations: string[];
  candidate: ObjectId | ObjectId[];
  filtersHash?: string;
  kind: string;
  event: string;
  model?: string;
}

export const useSendFeedbackToData = () => {
  const [state, execute, cancel] = axios<string>(
    {
      url: "/v2/offers/",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (data: IFeedbackData) => {
      return execute({
        url: "/v2/offers/feedback",
        data,
      });
    },
    [execute]
  );
  return [state, callback, cancel] as const;
};

export const sendFeedbackToData = (data: IFeedbackData) => {
  return axiosInstance.post<string>("/v2/offers/feedback", data);
};

export interface IOfferPayloadEnterprise {
  _id?: string;
  name?: string;
  isTemp?: boolean;
}

export interface IPutJobOfferPayload {
  offer: Omit<IOffer, "enterprise" | "contact" | "metadata" | "startDate" | "expirationDate"> & {
    startDate?: string;
    expirationDate?: string;
  };
  enterprise: IOfferPayloadEnterprise;
  contact?: Partial<IContact>;
}

export const usePutDataOffer = () => {
  const [state, execute, cancel] = axios<undefined>(
    {
      url: "/v2/offers/job",
      method: "PUT",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (id: string, data: IPutJobOfferPayload) => {
      return execute({
        url: `/v2/offers/job/${id}`,
        data,
      });
    },
    [execute]
  );
  return [state, callback, cancel] as const;
};

export interface ICreateOfferPayload {
  offer: ICreateOfferPayloadOffer;
  createOffer?: boolean;
  enterprise?: IOfferPayloadEnterprise;
  contact?: Partial<IContact>;
  starCandidate?: string;
}

export const usePostDataOffer = () => {
  const [state, execute, cancel] = axios<IOffer>(
    {
      url: "/v2/offers/job",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (data: ICreateOfferPayload) => {
      return execute({
        url: "/v2/offers/job",
        data,
      });
    },
    [execute]
  );
  return [state, callback, cancel] as const;
};

export const useGetOffersPinned = () => {
  const [state, execute, cancel] = axios(
    {
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback((candidateId) => execute({ url: `/v2/offers/pinned/${candidateId}` }), [execute]);
  return [state, callback, cancel] as const;
};

interface IUseGetOfferPinnedCandidatesBody {
  data: string[];
  total: number;
  count: number;
}

export const useGetOfferPinnedCandidates = () => {
  const [state, execute, cancel] = axios<IUseGetOfferPinnedCandidatesBody>(
    {
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback((offerId: string) => execute({ url: `/v2/offers/${offerId}/pinned` }), [execute]);
  return [state, callback, cancel] as const;
};

interface IUseGetOfferUrlBody {
  data: IOffer | null;
}

export const useGetOfferUrl = () => {
  const [state, execute, cancel] = axios<IUseGetOfferUrlBody>(
    {
      url: "/v2/offers/url",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (externalUrl: string) => execute({ params: { url: encodeURIComponent(externalUrl) } }),
    [execute]
  );
  return [state, callback, cancel] as const;
};

export const usePostOfferPinned = () => {
  const [state, execute, cancel] = axios<undefined>(
    {
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (candidateId: string, offerId: string, data?: { filtersHash: string }) =>
      execute({ url: `/v2/offers/pinned/${candidateId}/${offerId}`, data }),
    [execute]
  );
  return [state, callback, cancel] as const;
};

export const useDeleteOfferPinned = () => {
  const [state, execute, cancel] = axios<undefined>(
    {
      method: "DELETE",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (candidateId: string, offerId: string) => execute({ url: `/v2/offers/pinned/${candidateId}/${offerId}` }),
    [execute]
  );
  return [state, callback, cancel] as const;
};

export const useGetOffer = () => {
  const [state, execute, cancel] = axios<IOffer>(
    {
      url: "/v2/offers/{kind}/{id}",
      method: "get",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (kind: string, id: string) => {
      return execute({
        url: `/v2/offers/${kind}/${id}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export type TEnterpriseOffer = IOffer & { count: number };

interface IUseGetOffersEnterpriseBody {
  data: TEnterpriseOffer[];
  limit: number;
  skip: number;
  total: number;
  count: number;
  orderStrategy: string;
  metadata: Record<string, never>;
}

interface IUseGetOffersEnterpriseParams {
  source: "current" | "manual" | "hired";
  limit: number;
  skip: number;
}

export const useGetOffersEnterprise = () => {
  const [state, execute, cancel] = axios<IUseGetOffersEnterpriseBody>(
    {
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (enterpriseId: string, params: IUseGetOffersEnterpriseParams) =>
      execute({ url: `/v2/offers/search/job/enterprise/${enterpriseId}`, params }),
    [execute]
  );
  return [state, callback, cancel] as const;
};

export interface IGetFavoriteSocialOfferParams {
  candidateId: string;
}

export interface IGetFavoriteSocialOfferQueryParams {
  limit?: number;
  skip?: number;
  challenges?: string[];
}

export interface IGetFavoriteSocialOfferResponse {
  data: ISocialOfferWithFavoriteChallenges[];
  limit: number;
  skip: number;
  count: number;
  total: number;
}

export const useGetFavoriteSocialOffers = () => {
  const [state, execute, cancel] = axios<IGetFavoriteSocialOfferResponse, IGetFavoriteSocialOfferParams, unknown>(
    {
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (candidateId: string, params: IGetFavoriteSocialOfferQueryParams) =>
      execute({ url: `/offers/social/search/candidate/${candidateId}/favorite`, params }),
    [execute]
  );
  return [state, callback, cancel] as const;
};

export * from "./socialOffer/getSocialOffer";
export * from "./socialOffer/createSocialOffer";
export * from "./socialOffer/searchSocialOffers";
export * from "./socialOffer/updateSocialOffer";
export * from "./socialOffer/removeSocialOfferSpotlight";
export * from "./socialOffer/searchSocialOffersSpotlight";
export * from "./socialOffer/types";
export * from "./socialOffer/getSocialOfferApplyCount";
export * from "./socialOffer/getSocialApplyByStatus";
export * from "./archiveOffer";
export * from "./generateOfferFromIA";
export * from "./generateOfferFromIA/useGenerateOffersFromIA";
export * from "./trainingOffer/searchByCandidate";
export * from "./trainingOffer/createTrainingOffer";
export * from "./trainingOffer/putTrainingOffer";
export * from "./dashboard";
